import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { Loader2, SlidersHorizontal, X } from "lucide-react";
import { api } from "../api";
import { ApiError } from "../api/client";
import { isWireEnvelope } from "../api/unwrap";
import {
  qk,
  useSampleEvents,
  useFieldCovariates,
  useDesignateCurrentEvent,
} from "../hooks/queries";
import {
  pinState,
  eventStatusLabel,
  fmtEventDate,
  isPointShaped,
} from "../lib/sampleEvents";
import { classifyZonesByDecile } from "../lib/retestDecile";
import { withZoneNames } from "../lib/zoneExport";
import { pickedCaption, pickedSubtitle } from "../lib/mapSoilCopy";
import { schoolProps } from "../lib/schoolProperties";
import { SavingSoilMapNote } from "../features/saving-soil-map/SavingSoilMapNote";
import { IdwRippleWindow } from "../features/idw-ripple/IdwRippleWindow";
import { zoneUploadNote } from "../lib/uploadQueued";
import type { SampleEvent } from "../types/contract";
import { useUiStore } from "../stores/uiStore";
import { useLayerStore } from "../stores/layerStore";
import { usePointEditStore } from "../stores/pointEditStore";
import { useMapIntro } from "../stores/mapIntroStore";
import { showsAdvancedControls } from "../stores/uiModeStore";
import { useZoneCreateStore } from "../stores/zoneCreateStore";
import { useBehaviorZoneStore } from "../stores/behaviorZoneStore";
import { useGeniusCoreStore } from "../stores/geniusCoreStore";
import { useZoneCoreStore } from "../stores/zoneCoreStore";
import { useZoneEditStore } from "../stores/zoneEditStore";
import {
  cellUnderClick,
  CORE_PROPS,
  emptyCoreValues,
  valuesAtCell,
  zonesFromFc,
} from "../lib/geniusCores";
import {
  assignCoresToZones,
  CORE_FILE_ACCEPT,
  CORE_FILE_HINT,
  coreLocationsFromFiles,
  coreUploadBlock,
} from "../lib/geniusCoreUpload";
import {
  GENIUS_METHOD,
  GENIUS_SAVE_PROPS,
  geniusSaveBlock,
  geniusSaveCores,
} from "../lib/geniusMap";
import { stackCellData } from "../lib/stackClient";
import { useJobs } from "../hooks/useJobTracker";
import { JdLinkModal } from "./JdLinkModal";
import { MapSoilAnimation } from "./MapSoilAnimation";
import type { AnimVariant } from "../lib/mapSoilAnimation";
import { useUiMode } from "../stores/uiModeStore";
import { useAnimCells } from "../hooks/useAnimCells";
import { useEnhancedVisuals } from "../stores/enhancedVisualsStore";
import { ClassifyZonesModal } from "../features/classify-zones/ClassifyZonesModal";
import { JdImportStep } from "../features/jd-import/JdImportStep";
import {
  HighResInfo,
  HIGHRES_COMPARE_TO,
  HIGHRES_INTRO,
  HIGHRES_BULLETS,
} from "../features/high-res/HighResInfo";
import { MODEL_GAINS } from "../lib/modelGains";
import { MapSoilPropsOptions } from "../features/map-soil-props/MapSoilPropsOptions";
import { CsvPointsMapper } from "./CsvPointsMapper";
import {
  PreviewMap,
  type PreviewSchool,
} from "../features/preview-map/PreviewMap";
import {
  resolveReadingKeys,
  findOmModel,
  findGridOmIdw,
  resolveGridIdwReadings,
} from "../lib/previewReadings";
import { useLabOrder } from "../stores/labOrderStore";
import {
  useSamplingCampaign,
  type CampaignMode,
} from "../stores/samplingCampaign";
import {
  resolvePresetFCs,
  persistResolvedFCs,
  fcCount,
  type ResolvedFC,
} from "../lib/campaignOrder";
import { useSamplingConfirm } from "../stores/samplingConfirm";
import { downloadDesignedZones } from "../lib/zoneExport";
import { parseCsv, type ParsedCsv } from "../lib/csv";
import type { DashboardField } from "../types/contract";
import { Modal } from "../ui/Modal";

// Single-field only — a multi-field selection keeps the batch panel (mapping campaigns are
// inherently per-field).

function modelRunMethod(opts: {
  idw?: boolean;
  zonesFlat?: boolean;
}): "idw" | "zones_flat" | null {
  if (opts.zonesFlat) return "zones_flat";
  if (opts.idw) return "idw";
  return null;
}

const DESC = {
  zones:
    "Condenses field variation data into understandable, internally consistent zones. Choose how many zones you want, and it will automatically analyze the most influential data to make the zones as consistent as possible.",
  classicGrid:
    "Lay out sampling points in a tried & true grid pattern. Create the color map using a weighted average of nearby points.\n\nDefault Density: 1 sample per 2.5 acres.",
  custom:
    "Full manual control — choose exactly which data layers (and their weights) feed the model, which soil properties to map, and which sample event to use. For advanced users.",
} as const;

// Behavior-classified zones description: the FIRST letter of each category word is bolded +
// underlined (matching the zone letter it maps to — "W" for Wet, "I" for Infertile).
const firstBold = (w: string) => (
  <>
    <span className="font-bold underline">{w[0]}</span>
    {w.slice(1)}
  </>
);
const BEHAVIOR_DESC = (
  <>
    Zones are behavior classes consistent across farms &amp; regions — all
    “WPFs” are “Wet, Productive &amp; Fertile”.
    <br />
    Names are 3 letters:
    <br />
    1st Letter: {firstBold("Wet")} / {firstBold("Mid")} / {firstBold("Dry")}
    <br />
    2nd: {firstBold("Productive")} / {firstBold("Mid")} /{" "}
    {firstBold("Unproductive")}
    <br />
    3rd: {firstBold("Fertile")} / {firstBold("Mid")} / {firstBold("Infertile")}
  </>
);

function errMsg(e: unknown): string {
  if (e instanceof ApiError) return e.message;
  return e instanceof Error ? e.message : "Something went wrong.";
}

const SERVER_DROPPED = "The server dropped this request — try again.";

// A 5xx body that is not the `{data,error,meta}` envelope has no message of ours.
function serverDropped(e: unknown): boolean {
  if (!axios.isAxiosError(e)) return false;
  const status = e.response?.status;
  if (status == null || status < 500 || status > 599) return false;
  return !isWireEnvelope(e.response?.data);
}

const SAMPLES_PROCESSING =
  "Samples are still being processed for this field. We'll be ready in a few minutes; try again shortly.";
const SAMPLES_UPLOADING =
  "Samples are still uploading for this field. We'll be ready in a few minutes; try again shortly.";

function sampleWaitCopy(code: string): string | null {
  if (code === "samples_normalizing") return SAMPLES_PROCESSING;
  if (code === "samples_ingesting") return SAMPLES_UPLOADING;
  return null;
}

type View =
  | "menu"
  | "picker"
  | "custom"
  | "uploadPoints"
  | "uploadZones"
  | "uploadCores"
  | "jd"
  | "jdlink"
  | "highresInfo"
  | "classifyZones"
  | "layerpick"
  | "behaviorConfirm";

/*
 *
 * THE THREE-WAY MODE: 'create' = "Create New Points/Zones" (the old "Order Soil Test"
 * byte-for-byte), 'retest' = "Order Retest" (NEW — re-order sampling on an EXISTING point/zone
 * layer), 'results' = "Map With Existing Test Results" (the old "Upload My Own Samples", reworked
 * around the same per-field layer picker).
 */
type MapSoilMode = "create" | "retest" | "results";

/* * one field's picked existing layer (modes 'retest' and 'results') */
interface PickedLayer {
  eventId: string;
  /*
   *
   * open vocabulary — mirrors `SampleEvent['kind']`; NEVER branch geometry on it.
   */
  kind: SampleEvent["kind"];
  /*
   * the geometry decision, made ONCE at pick time via `isPointShaped` (BE-Light's
   * `geometry_kind` when present, else the `!== 'zones'` fallback)
   */
  pointShaped: boolean;
  name: string;
  count: number;
  fc: GeoJSON.FeatureCollection | null;
}

export function MapSoilPanel({
  fields,
  onClose,
}: {
  fields: DashboardField[];
  onClose: () => void;
}) {
  const field = fields[0];
  const multi = fields.length > 1;
  const qc = useQueryClient();
  const { data: events } = useSampleEvents(field.field_id);
  // change WHICH campaign is current (pin / clear-pin) + detect a pin that's holding back newer samples
  const designate = useDesignateCurrentEvent();
  const pin = useMemo(() => pinState(events), [events]);
  const { data: covs } = useFieldCovariates(field.field_id);
  const sortedEvents = useMemo(
    () =>
      [...(events ?? [])].sort((a, b) =>
        b.created_at.localeCompare(a.created_at),
      ),
    [events],
  );
  // "JD data absent" = no machine covariate on the field carries values yet. While the catalog is
  // still loading we err toward SHOWING the optional JD step (it's dismissible with Skip) rather than
  // silently launching the destructive not-existing High-Res path before we know the JD state.
  const jdAbsent = useMemo(
    () => !(covs ?? []).some((c) => c.category === "machine" && c.has_values),
    [covs],
  );

  const [mode, setMode] = useState<MapSoilMode>("create");
  // DERIVED, NOT DELETED: every 'use existing samples' branch below serves exactly the 'results'
  // never enters these branches.
  const useExisting = mode === "results";
  const [eventId, setEventId] = useState<string | null>(null);
  // THE FIRST PICK LOCKS THE TYPE — later fields only offer that kind, so every field ends up with
  // the same kind of layer.
  const [picks, setPicks] = useState<Record<string, PickedLayer>>({});
  const [pickIndex, setPickIndex] = useState(0);
  const [seqEvents, setSeqEvents] = useState<Record<string, SampleEvent[]>>({});
  // WALKED IN SELECTION ORDER, NOT KEY ORDER: the FIRST PICKED field in the SELECTION is the lock.
  const lockedPick = useMemo(
    () =>
      fields.map((f) => picks[f.field_id]).find((p): p is PickedLayer => !!p) ??
      null,
    [picks, fields],
  );
  const lockedPointShaped = lockedPick?.pointShaped ?? null; // the GEOMETRY — the lock, everywhere
  // the lock is by SHAPE, never by id namespace — post-handoff: locking on `kind` meant a
  // bulk-ingested point set (kind 'bulk') on field 1 HID field 2's `points:` layers two
  // point-shaped layers that belong in one run could not be picked together.
  const lockedWord =
    lockedPointShaped == null ? null : lockedPointShaped ? "point" : "zone";
  const [view, setView] = useState<View>("menu");
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<{
    text: string;
    err: boolean;
    retry?: () => void;
  } | null>(null);
  const [csvDraft, setCsvDraft] = useState<ParsedCsv | null>(null);
  // which school opened the "upload points" step → the map it dispatches after saving:
  // 'ml' = High-Resolution (trained ensemble), 'idw' = Classic Grid (purely spatial IDW p=3)
  const [uploadMethod, setUploadMethod] = useState<"ml" | "idw">("ml");
  // multi-field upload sequence: upload each selected field's points in turn, then build all.
  const [uploadSeq, setUploadSeq] = useState<{
    method: "ml" | "idw";
    index: number;
  } | null>(null);
  const [highresModel, setHighresModel] = useState<string | null>(null); // which model the High-Res info opens on
  // PURELY DECORATIVE: it is opened beside the dispatch, never in front of it, and closing it
  // cannot reach the job.
  const [animOpen, setAnimOpen] = useState<AnimVariant | null>(null);
  // Map-With-Existing: the Behavior window LOOPS its three indexes until closed; the Relative pick
  // shows a two-second "Saving as Soil Map" bar instead of the preset story.
  const [animLoop, setAnimLoop] = useState(false);
  const [savingOpen, setSavingOpen] = useState(false);
  // Map-With-Existing → Classic Grid: the "Running IDW Interpolation" ripple window fed by the
  // PICKED points layer — no prepared animation plane.
  const [rippleOpen, setRippleOpen] = useState(false);
  // The customize handler for whichever animation is open — bound at click time, because each school
  // has its own editing flow and the window only knows that it has a Customize button.
  const animCustomizeRef = useRef<
    ((fc?: GeoJSON.FeatureCollection) => void) | null
  >(null);
  const enhancedVisuals = useEnhancedVisuals((s) => s.enabled);
  const uiMode = useUiMode((st) => st.mode);
  // Even a ~90 KB manifest read has a round trip in it, so the work moves to the moment the panel
  // opens, while the user is still reading the preset cards — by click time the frames are in
  // memory and the map appears WITH the title.
  const anim = useAnimCells(field?.field_id, enhancedVisuals);
  const zonesFileRef = useRef<HTMLInputElement>(null);
  const coresFileRef = useRef<HTMLInputElement>(null);
  const customizeSeedRef = useRef<GeoJSON.FeatureCollection | null>(null);

  const selectedEvent =
    sortedEvents.find((e) => e.event_id === eventId) ?? null;

  // Adopt the SERVED build-time AutoMap points (what the preview scored + what a preset-click orders) as the
  // point editor's starting design, so Customize opens on the SAME plan the card previews — identical Accuracy
  // Index. Falls back to a fresh client-side suggest / grid layout when the served FC isn't available.
  // Has the user already watched the animation this trip? Travels with `customizeSeedRef` through the
  // optional JD-import detour, for the same reason: both describe the click that started the flow.
  const introSeenRef = useRef(false);
  const queuedFieldsRef = useRef<Set<string>>(new Set());

  function seedPointEditorFromServed(
    fc: GeoJSON.FeatureCollection | null,
    grid: boolean,
  ) {
    const pts = (fc?.features ?? [])
      .map((f) =>
        f.geometry?.type === "Point"
          ? (f.geometry.coordinates as number[])
          : null,
      )
      .filter((c): c is number[] => Array.isArray(c) && c.length >= 2)
      .map((c) => ({ lng: c[0], lat: c[1] }));
    const store = usePointEditStore.getState();
    if (pts.length) {
      store.start(field.field_id, grid ? { grid: true } : undefined); // grid → keep gridMode for the accuracy kind + IDW build
      usePointEditStore.setState({ pendingSuggest: false, pendingGrid: false }); // use the served design, don't regenerate
      store.seed(pts);
    } else {
      store.start(field.field_id, grid ? { grid: true } : { suggest: true }); // no served FC → regenerate client-side
    }
  }

  // later fields' events for the sequential picker (fields[0] rides the existing hook)
  useEffect(() => {
    if (view !== "layerpick") return;
    const f = fields[pickIndex];
    if (!f || f.field_id === field.field_id || seqEvents[f.field_id]) return;
    let alive = true;
    void api
      .fetchSampleEvents(f.field_id)
      .then((evs) => {
        if (alive) setSeqEvents((p) => ({ ...p, [f.field_id]: evs }));
      })
      .catch(() => {
        if (alive) setSeqEvents((p) => ({ ...p, [f.field_id]: [] }));
      });
    return () => {
      alive = false;
    };
  }, [view, pickIndex, fields, field.field_id, seqEvents]);

  const switchMode = (m: MapSoilMode) => {
    setMode(m);
    setNote(null);
    setPicks({});
    setPickIndex(0);
    if (m === "create") {
      setEventId(null);
      return;
    }
    // both non-create modes start by picking each field's existing layer — the picker is the mode
    setView("layerpick");
  };

  // --- High-Resolution Map / Classic Grid map ------------------------------------------
  // Dispatch a soil-property map build. `idw` → the "Classic Grid (Purely Spatial)" school:
  // inverse-distance interpolation (power 3) between the points, no ML ensemble, no other layers.
  async function dispatchModelRun(
    sampleEventId: string | null,
    opts: { idw?: boolean; zonesFlat?: boolean } = {},
    targetId: string = field.field_id,
  ) {
    setBusy(true);
    setNote(null);
    try {
      const runOpts = {
        ...(sampleEventId ? { sample_event_id: sampleEventId } : {}),
        ...(opts.idw ? { method: "idw" as const, idw_power: 3 } : {}),
        // compute's zone-mean lane: every pixel in a zone = that zone's reading, no interpolation.
        ...(opts.zonesFlat ? { method: "zones_flat" as const } : {}),
      };
      // Genius (no method) stays the five trained targets. Zone schools add buffer pH.
      // Classic Grid does not come through here: saveIdwMap omits properties and the
      // server's idw default writes the sixth layer.
      const method = modelRunMethod(opts);
      const job = await api.runModel(
        targetId,
        [...schoolProps(method)],
        runOpts,
      );
      if (job?.job_id) {
        useJobs.getState().upsert({
          job_id: job.job_id,
          pct: 0,
          status: "running",
          message: opts.zonesFlat
            ? "Rendering zone readings…"
            : opts.idw
              ? "Interpolating (IDW) soil maps…"
              : "Mapping soil properties…",
        });
      }
      setNote({
        text: "Model running — the maps appear in the field’s layer list when ready.",
        err: false,
      });
    } catch (e) {
      setNote({ text: errMsg(e), err: true });
    } finally {
      setBusy(false);
    }
  }

  /*
   *
   * BOTH ENTRY POINTS LAND HERE: *"Since there are no customization options for the high-res map if
   * using pre-existing samples, the same animation should play after the user clicks 'customize' as
   * after they hit the normal preset.
   */
  function runHighResModel(sampleEventId: string) {
    if (enhancedVisuals) setAnimOpen("map");
    void dispatchModelRun(sampleEventId);
  }

  // Multi-field UPLOAD sequence — branches 3/4/5: with >1 field in "Upload My Own Samples" mode
  // collect each field's points one at a time, then build the map (ML or IDW) for every field.
  function startUploadSeq(method: "ml" | "idw") {
    queuedFieldsRef.current = new Set();
    setUploadSeq({ method, index: 0 });
    setView("uploadPoints");
  }

  // The actual High-Res action, run AFTER the optional JD-import step.
  function proceedHighRes() {
    if (useExisting) {
      // ONLY a points event can feed a soil-property model run (a zones event can't); and gate on
      // `selectedEvent` (resolved from the live list) not the raw id, so a since-removed event
      // routes to upload rather than dispatching a stale/wrong id. isPointShaped, NOT `kind ===
      // 'points'` — a bulk-ingested point set is kind 'bulk' and used to fall through to the upload
      // screen instead of running the model.
      if (selectedEvent && isPointShaped(selectedEvent))
        return void runHighResModel(selectedEvent.event_id);
      // using existing samples but no usable points event selected → upload point results, then map
      setUploadMethod("ml");
      setView("uploadPoints");
      return;
    }
    // no existing samples → design a sampling plan on the map, approve + order. Seeded from the served AutoMap
    // (matching the preview) when Customize supplied it, else auto-suggested. Does NOT create a map yet.
    useZoneCreateStore.getState().end();
    useBehaviorZoneStore.getState().end();
    useZoneEditStore.getState().end();
    const seed = customizeSeedRef.current;
    customizeSeedRef.current = null;
    seedPointEditorFromServed(seed, false);
    if (enhancedVisuals && !introSeenRef.current)
      useMapIntro.getState().arm(field.field_id, "sample");
    introSeenRef.current = false;
    useUiStore.getState().openMap(field.field_id);
    onClose();
  }

  function onHighRes(fc?: GeoJSON.FeatureCollection, seenIntro = false) {
    customizeSeedRef.current = fc ?? null; // remember the served design across the (optional) JD-import detour
    introSeenRef.current = seenIntro; // and whether the animation has already been watched
    if (multi && !useExisting) {
      startCampaign("suggest");
      return;
    } // >1 field → per-field stepper
    if (multi && useExisting) {
      void runForPicks(false);
      return;
    } // >1 field → build every field from its PICKED event
    if (jdAbsent) {
      setView("jd");
      return;
    }
    proceedHighRes();
  }

  // Classic Grid is saved in this request. The ripple stays up while it runs.
  async function saveIdwTarget(fieldId: string, eventId: string | null) {
    await api.saveIdwMap(fieldId, eventId);
  }

  async function saveClassicGrid(
    targets: { fieldId: string; eventId: string | null }[],
  ) {
    if (enhancedVisuals) setRippleOpen(true);
    setBusy(true);
    setNote(null);
    try {
      for (let i = 0; i < targets.length; i++) {
        const target = targets[i];
        if (!target) continue;
        if (targets.length > 1) {
          setNote({
            text: `Saving Classic Grid map ${i + 1} of ${targets.length}…`,
            err: false,
          });
        }
        await saveIdwTarget(target.fieldId, target.eventId);
        void qc.invalidateQueries({ queryKey: qk.layers(target.fieldId) });
      }
      setNote({
        text:
          targets.length > 1
            ? `Classic Grid maps saved for all ${targets.length} fields.`
            : "Classic Grid map saved — it is in the field’s layer list.",
        err: false,
      });
    } catch (e) {
      const wait = e instanceof ApiError ? sampleWaitCopy(e.code) : null;
      if (wait) {
        setNote({
          text: wait,
          err: false,
          retry: () => {
            void saveClassicGrid(targets);
          },
        });
        return;
      }
      if (serverDropped(e)) {
        setNote({
          text: SERVER_DROPPED,
          err: true,
          retry: () => {
            void saveClassicGrid(targets);
          },
        });
        return;
      }
      setNote({ text: errMsg(e), err: true });
    } finally {
      setBusy(false);
    }
  }

  // ---- mode 'results': build every field from its picked event -----------------------------------
  async function runForPicks(idw: boolean) {
    const missing = fields.filter((f) => !picks[f.field_id]?.eventId);
    if (missing.length) {
      setNote({
        text: "Pick a sample layer for every field first.",
        err: true,
      });
      setView("layerpick");
      return;
    }
    // the animation opens BESIDE the dispatches, never in front of them (the standing rule).
    if (idw) {
      await saveClassicGrid(
        fields.map((f) => ({
          fieldId: f.field_id,
          eventId: picks[f.field_id]!.eventId,
        })),
      );
      return;
    }
    if (enhancedVisuals) setAnimOpen("map");
    for (const f of fields)
      await dispatchModelRun(picks[f.field_id]!.eventId, {}, f.field_id);
  }

  // mode 'retest': straight to the PRICING popup, no animations The picked geometry IS the design
  // being ordered; persisting mints a new event/target set exactly as every other order does —
  // there is no re-order-the-same-event lane anywhere in the system.
  function retestOrder(
    kind: "points" | "zones",
    override?: Record<string, PickedLayer>,
  ) {
    const use = override ?? picks;
    const usable = fields
      .map((f) => ({
        field: { field_id: f.field_id, field_name: f.field_name },
        fc: use[f.field_id]?.fc ?? null,
      }))
      .filter(
        (
          r,
        ): r is {
          field: { field_id: string; field_name: string };
          fc: GeoJSON.FeatureCollection;
        } => !!r.fc?.features?.length,
      );
    if (usable.length !== fields.length) {
      setNote({
        text: "Every field needs a picked layer with geometry first.",
        err: true,
      });
      setView("layerpick");
      return;
    }
    const when = new Date().toISOString();
    useSamplingConfirm.getState().request({
      items: usable.map((r) => ({
        field_id: r.field.field_id,
        field_name: r.field.field_name,
        kind,
        count: fcCount(r.fc),
      })),
      ...(usable[0]?.fc
        ? { preview: { field_id: usable[0].field.field_id, fc: usable[0].fc } }
        : {}),
      ...(kind === "zones"
        ? {
            download: async () => {
              for (const r of usable)
                await downloadDesignedZones(r.field, r.fc);
            },
          }
        : {}),
      run: async (action) => {
        const lab = useLabOrder.getState().snapshot();
        const results = await persistResolvedFCs(
          usable.map((r) => ({ field: r.field, fc: r.fc })),
          kind,
          action,
          lab,
          when,
        );
        void qc.invalidateQueries({ queryKey: qk.workOrders });
        void qc.invalidateQueries({ queryKey: qk.fields });
        for (const r of results) {
          void qc.invalidateQueries({
            queryKey: qk.sampleEvents(r.field.field_id),
          });
          void qc.invalidateQueries({ queryKey: qk.layers(r.field.field_id) });
        }
        const failed = results.filter((r) => !r.ok);
        if (failed.length)
          throw new Error(
            `${results.length - failed.length}/${results.length} done — failed: ${failed.map((r) => r.field.field_name).join(", ")} (${failed[0].error})`,
          );
        onClose();
        if (action === "order") useUiStore.getState().openWorkOrders();
        else useUiStore.getState().openMap(field.field_id);
      },
    });
  }

  // mode 'results' + a ZONE school: the flat zone-mean build — compute lane.
  async function resultsRunZonesFlat(
    variant: "behavior" | "relative",
    opts: { windowOpen?: boolean } = {},
  ) {
    const missing = fields.filter((f) => !picks[f.field_id]?.eventId);
    if (missing.length) {
      setNote({ text: "Pick a zone layer for every field first.", err: true });
      setView("layerpick");
      return;
    }
    // the visual aid, gated on Enhanced Visualizations like every animation: Relative → the
    // two-second "Saving as Soil Map" pop-up; Behavior → the three-index window, looping until the
    // user closes it (opened on "confirm" by the caller, before the classification — `windowOpen`).
    if (enhancedVisuals && !opts.windowOpen) {
      if (variant === "relative") setSavingOpen(true);
      else {
        setAnimLoop(true);
        setAnimOpen(variant);
      }
    }
    for (const f of fields)
      await dispatchModelRun(
        picks[f.field_id]!.eventId,
        { zonesFlat: true },
        f.field_id,
      );
  }

  // Behavior + results: confirm → classify (rename) → SAVE the renamed zones → flat build.
  async function resultsBehaviorConfirmed() {
    setBusy(true);
    setNote(null);
    // THE WINDOW OPENS ON "CONFIRM", not after the work — "it should start righ away after you
    // press confirm, instead of waiting a few seconds").
    setView("menu");
    if (enhancedVisuals) {
      setAnimLoop(true);
      setAnimOpen("behavior");
    }
    try {
      for (const f of fields) {
        const pick = picks[f.field_id];
        if (!pick?.fc)
          throw new Error(`${f.field_name}: no zone geometry picked`);
        const out = await classifyZonesByDecile(f.field_id, pick.fc);
        await api.saveZones(f.field_id, withZoneNames(out.fc));
        void qc.invalidateQueries({ queryKey: qk.layers(f.field_id) });
      }
      await resultsRunZonesFlat("behavior", { windowOpen: true });
    } catch (e) {
      // the story window is not a claim of success — a failed classify/save closes it and says why
      setAnimOpen(null);
      setAnimLoop(false);
      setNote({ text: errMsg(e), err: true });
    } finally {
      setBusy(false);
    }
  }

  // Behavior retest: confirm first (the zones get RENAMED by the decile classification), then classify
  // every field's picked zones and price the renamed set.
  async function retestBehaviorConfirmed() {
    setBusy(true);
    setNote(null);
    try {
      const renamed: Record<string, PickedLayer> = { ...picks };
      for (const f of fields) {
        const pick = picks[f.field_id];
        if (!pick?.fc)
          throw new Error(`${f.field_name}: no zone geometry picked`);
        const out = await classifyZonesByDecile(f.field_id, pick.fc);
        renamed[f.field_id] = { ...pick, fc: out.fc };
      }
      setPicks(renamed);
      setView("menu");
      retestOrder("zones", renamed);
    } catch (e) {
      setNote({ text: errMsg(e), err: true });
      setView("menu");
    } finally {
      setBusy(false);
    }
  }

  // ---- retest CUSTOMIZE: the picked layer seeds the editor ----------------------------------------
  function retestCustomizePoints(grid: boolean) {
    const pick = picks[field.field_id];
    if (!pick?.fc?.features?.length) {
      setNote({ text: "That layer has no point geometry to edit.", err: true });
      return;
    }
    useZoneCreateStore.getState().end();
    useBehaviorZoneStore.getState().end();
    useZoneEditStore.getState().end();
    seedPointEditorFromServed(pick.fc, grid);
    // no map intro arm: retest is an ordering flow and specified NO animations on it.
    useUiStore.getState().openMap(field.field_id);
    onClose();
  }
  function resultsCustomizeGeniusZones() {
    const pick = picks[field.field_id];
    if (!pick?.fc?.features?.length) {
      setNote({
        text: "That layer has no zone geometry to place cores in.",
        err: true,
      });
      return;
    }
    if (!zonesFromFc(pick.fc).length) {
      setNote({
        text: "That layer has no zone polygons to place cores in.",
        err: true,
      });
      return;
    }
    usePointEditStore.getState().end();
    useBehaviorZoneStore.getState().end();
    useZoneCreateStore.getState().end();
    useZoneEditStore.getState().end();
    useGeniusCoreStore
      .getState()
      .start(field.field_id, field.field_name, pick.fc, pick.eventId);
    useUiStore.getState().openMap(field.field_id);
    onClose();
  }

  // Map With Existing → Genius Zones card body: a cores file, then the same save the walk's
  // "Save map" runs. The chip beside it ("Pick Core Points") is the manual walk.
  function openGeniusCoresUpload() {
    const pick = picks[field.field_id];
    if (!pick?.fc?.features?.length || !zonesFromFc(pick.fc).length) {
      setNote({ text: "Pick a zone layer before uploading cores.", err: true });
      return;
    }
    setNote(null);
    setView("uploadCores");
  }

  async function runGeniusFromCoreFile(file: File) {
    const pick = picks[field.field_id];
    const zones = zonesFromFc(pick?.fc);
    if (!pick?.eventId || !zones.length) {
      setNote({ text: "Pick a zone layer before uploading cores.", err: true });
      return;
    }
    setBusy(true);
    setNote(null);
    try {
      const parsed = await coreLocationsFromFiles([file]);
      const blocked = coreUploadBlock(parsed);
      if (blocked) {
        setNote({ text: blocked, err: true });
        return;
      }
      const { placed, outside } = assignCoresToZones(zones, parsed.points);
      if (!placed.length) {
        setNote({
          text: "None of those points fall inside the zones.",
          err: true,
        });
        return;
      }
      const keys = CORE_PROPS.map((p) => p.stack);
      const cell = await stackCellData(field.field_id, keys);
      const columns: Record<string, (number | null)[]> = {};
      keys.forEach((k, i) => {
        columns[k] = cell.features[i] ?? [];
      });
      const read = (lng: number, lat: number) => {
        const i = cellUnderClick(cell.geoms, cell.centroids, lng, lat);
        return i < 0 ? emptyCoreValues() : valuesAtCell(columns, i);
      };
      const placements = zones.map(
        () =>
          [] as {
            lng: number;
            lat: number;
            values: ReturnType<typeof emptyCoreValues>;
          }[],
      );
      for (const p of placed)
        placements[p.zoneIndex]?.push({
          lng: p.lng,
          lat: p.lat,
          values: read(p.lng, p.lat),
        });
      const labs = await api.fetchSoilSamples(field.field_id);
      const saveBlock = geniusSaveBlock(placements, zones, pick.eventId, true);
      if (saveBlock) {
        setNote({ text: saveBlock, err: true });
        return;
      }
      const job = await api.runModel(field.field_id, GENIUS_SAVE_PROPS, {
        method: GENIUS_METHOD,
        sample_event_id: pick.eventId,
        cores: geniusSaveCores(placements, zones, labs, pick.eventId),
        ...(cell.grid.contentVersion != null
          ? { stack_content_version: String(cell.grid.contentVersion) }
          : {}),
      });
      if (job?.job_id)
        useJobs.getState().upsert({
          job_id: job.job_id,
          pct: 0,
          status: "running",
          message: "Saving the Genius Zones map…",
        });
      setView("menu");
      const skipped = outside
        ? ` ${outside} ${outside === 1 ? "point was" : "points were"} outside every zone.`
        : "";
      setNote({
        text: `Mapping from ${placed.length} core${placed.length === 1 ? "" : "s"}.${skipped} The soil map shows in Visualize as “(Genius Zones)” when it finishes.`,
        err: false,
      });
    } catch (e) {
      setNote({ text: errMsg(e), err: true });
    } finally {
      setBusy(false);
    }
  }

  function retestCustomizeZones() {
    const pick = picks[field.field_id];
    if (!pick?.fc?.features?.length) {
      setNote({ text: "That layer has no zone geometry to edit.", err: true });
      return;
    }
    usePointEditStore.getState().end();
    useBehaviorZoneStore.getState().end();
    useZoneCreateStore.getState().end();
    // the picked zones are ADOPTED as the starting design (previewFc → pendingServedFc); clicking
    // any preset inside the editor re-designs and DISCARDS them — exact spec, and it is the
    // editor's existing one-shot servedLabels mechanism rather than new machinery.
    useZoneCoreStore.getState().clearDraft(field.field_id);
    useZoneEditStore.getState().start(field.field_id, "relative", {
      fieldName: field.field_name,
      k: pick.fc.features.length >= 2 ? pick.fc.features.length : null,
      boundaryAcres: field.acres,
      previewFc: pick.fc,
    });
    useUiStore.getState().openMap(field.field_id);
    onClose();
  }

  // ---- the per-field layer picker (modes 'retest' + 'results') ------------------------------------
  async function pickLayer(f: DashboardField, ev: SampleEvent) {
    setBusy(true);
    setNote(null);
    try {
      let fc: GeoJSON.FeatureCollection | null = null;
      const resolveId = async (layerId: string | null) => {
        if (!layerId) return null;
        try {
          const detail = await api.fetchLayerDetail(f.field_id, layerId);
          return (detail.inline ?? null) as GeoJSON.FeatureCollection | null;
        } catch {
          return null;
        }
      };
      // Routing a hypothetical areal non-`zones:` event down here would fall through to the ACTIVE
      // zones row and serve DIFFERENT polygons under the right name — the
      // wrong-thing-that-looks-right this function already refuses.
      if (ev.kind === "zones") {
        // A FAILED per-version fetch NEVER falls back to the active geometry: the user picked a
        // SPECIFIC version, and quietly substituting different polygons under the right name is the
        // wrong-thing-that-looks-right failure this panel keeps refusing.
        const versionId = ev.event_id.startsWith("zones:")
          ? ev.event_id.slice("zones:".length)
          : null;
        if (versionId) {
          try {
            fc = await api.fetchZoneVersionGeojson(f.field_id, versionId);
          } catch {
            fc = null;
          }
        } else {
          fc = await resolveId("zones");
        }
        // ORDER BY zone_number, ASSERTED rather than assumed — compute's request: their attribution
        // walks zones in zone_number order, and the decile classifier labels by FC order sorting
        // here is what makes "zone 2" mean the same zone on both sides of the seam.
        if (fc?.features?.length) {
          const zn = (f2: { properties?: Record<string, unknown> | null }) => {
            const raw = f2.properties?.zone_number ?? f2.properties?.zone;
            return typeof raw === "number" && Number.isFinite(raw)
              ? raw
              : Number.MAX_SAFE_INTEGER;
          };
          fc = {
            ...fc,
            features: [...fc.features].sort((x, y) => zn(x) - zn(y)),
          };
        }
      } else {
        const setId = ev.event_id.includes(":")
          ? ev.event_id.split(":")[1]!
          : ev.event_id;
        fc = await resolveId(`target-points-${setId}`);
        if (!fc) {
          const listed = await api
            .fetchFieldLayerList(f.field_id)
            .catch(() => null);
          const match = listed?.document?.layers?.find(
            (layer) =>
              layer.id.startsWith("target-points-") && layer.id.includes(setId),
          );
          if (match && match.id !== `target-points-${setId}`)
            fc = await resolveId(match.id);
        }
        if (!fc && ev.has_results) {
          // a results-bearing event whose design predates target layers — rebuild the points from the samples
          const pts = await api.fetchSoilSamples(f.field_id);
          const mine = pts.filter(
            (p2) => (p2.sample_event_id ?? null) === ev.event_id,
          );
          if (mine.length)
            fc = {
              type: "FeatureCollection",
              features: mine.map((p2) => ({
                type: "Feature",
                properties: { num: p2.sample_id ?? p2.point_id },
                geometry: { type: "Point", coordinates: [p2.lon, p2.lat] },
              })),
            };
        }
      }
      // results mode refuses a geometry-less AREAL pick too: storing the pick with fc:null let
      // PreviewMap fall back to the field's AutoMap polygons (`overrideFc ? ? automapFc`) —
      // DIFFERENT zones rendered under the picked layer's name, the exact
      // wrong-thing-that-looks-right the no-fallback rule above exists to prevent.
      if (!fc?.features?.length && (mode === "retest" || !isPointShaped(ev))) {
        setNote({
          text: `${ev.name}: no geometry is retrievable for that layer yet.`,
          err: true,
        });
        return;
      }
      setPicks((prev) => ({
        ...prev,
        [f.field_id]: {
          eventId: ev.event_id,
          kind: ev.kind,
          pointShaped: isPointShaped(ev),
          name: ev.name,
          count: ev.count,
          fc,
        },
      }));
      if (mode === "results" && f.field_id === field.field_id)
        setEventId(ev.event_id);
      if (pickIndex + 1 < fields.length) setPickIndex(pickIndex + 1);
      else setView("menu");
    } catch (e) {
      setNote({ text: errMsg(e), err: true });
    } finally {
      setBusy(false);
    }
  }

  // Multi-field CUSTOM stepper: open the map for the WHOLE selection and start a sampling campaign
  // CampaignStepBar drives the in-map editor one field at a time (Previous / Next / Save & order
  // all).
  function startCampaign(mode: CampaignMode) {
    usePointEditStore.getState().end();
    useZoneCreateStore.getState().end();
    useBehaviorZoneStore.getState().end();
    useSamplingCampaign.getState().startCustom(
      mode,
      fields.map((f) => ({
        field_id: f.field_id,
        field_name: f.field_name,
        acres: f.acres,
      })),
    );
    useUiStore.getState().openMapFields(fields.map((f) => f.field_id));
    onClose();
  }

  // PRESET (single map-click OR multi-field "run on all"): resolve each field's default design
  // then open the confirmation dialog showing the total across fields; on Save/Save & order it
  // persists (± orders) all of them.
  async function confirmPresetOrder(
    kind: "points" | "zones",
    mode: CampaignMode,
    singleFc?: GeoJSON.FeatureCollection,
    schoolAutomapId?: string,
  ) {
    const when = new Date().toISOString();
    const cf = fields.map((f) => ({
      field_id: f.field_id,
      field_name: f.field_name,
    }));
    let resolved: ResolvedFC[];
    if (!multi && singleFc) {
      resolved = [{ field: cf[0], fc: singleFc }];
    } else {
      setBusy(true);
      setNote(null);
      resolved = await resolvePresetFCs(cf, mode, schoolAutomapId);
      setBusy(false);
    }
    const usable = resolved.filter((r) => r.fc && r.fc.features?.length);
    if (!usable.length) {
      setNote({
        text: "That preview isn’t ready yet — use Customize to design it.",
        err: true,
      });
      return;
    }
    useSamplingConfirm.getState().request({
      items: usable.map((r) => ({
        field_id: r.field.field_id,
        field_name: r.field.field_name,
        kind,
        count: fcCount(r.fc),
      })),
      // the FIRST field's design, for the animation to draw. It rides the request rather than being
      // resolved a second time — two resolutions could disagree, and the user would be looking at
      // points that are not the ones being priced.
      ...(usable[0]?.fc
        ? { preview: { field_id: usable[0].field.field_id, fc: usable[0].fc } }
        : {}),
      // zones only: "Save & Download" saves then downloads each field's zone file (points have no file export)
      ...(kind === "zones"
        ? {
            download: async () => {
              for (const r of usable)
                await downloadDesignedZones(r.field, r.fc!);
            },
          }
        : {}),
      run: async (action) => {
        const lab = useLabOrder.getState().snapshot();
        const results = await persistResolvedFCs(
          usable,
          kind,
          action,
          lab,
          when,
        );
        void qc.invalidateQueries({ queryKey: qk.workOrders });
        void qc.invalidateQueries({ queryKey: qk.fields });
        for (const r of results) {
          void qc.invalidateQueries({
            queryKey: qk.sampleEvents(r.field.field_id),
          });
          void qc.invalidateQueries({ queryKey: qk.layers(r.field.field_id) });
        }
        const failed = results.filter((r) => !r.ok);
        if (failed.length)
          throw new Error(
            `${results.length - failed.length}/${results.length} done — failed: ${failed.map((r) => r.field.field_name).join(", ")} (${failed[0].error})`,
          );
        onClose();
        if (action === "order") {
          useUiStore.getState().openWorkOrders();
        } else {
          useUiStore.getState().openMap(field.field_id); // Save-only → show the map + reveal its new Target Sampling Layer
          const reveal = results.find((r) => r.layerId)?.layerId;
          if (reveal) useLayerStore.getState().setVisible(reveal, true);
        }
      },
    });
  }

  // --- Multi-Input Zones --------------------------------------------------------------
  function onZones(fc?: GeoJSON.FeatureCollection) {
    if (multi && !useExisting) {
      startCampaign("zones");
      return;
    } // >1 field → per-field stepper
    if (useExisting) {
      if (selectedEvent) {
        setNote(
          selectedEvent.kind === "zones"
            ? {
                text: `Using the saved zones “${selectedEvent.name}”.`,
                err: false,
              }
            : {
                text: `“${selectedEvent.name}” is a points event — pick a zones event, or draw new zones.`,
                err: true,
              },
        );
        return;
      }
      setView("uploadZones"); // using existing but none selected → upload a zone file
      return;
    }
    // no existing zones → the interactive CUSTOMIZE editor: pick + weight layers, re-cluster live, cell-edit zones.
    usePointEditStore.getState().end();
    useBehaviorZoneStore.getState().end();
    useZoneCreateStore.getState().end();
    // suggestZoneCount` IS live and, since compute's fix, returns the same design-verified count
    // it stays the FALLBACK for the paths that don't pre-seed k (campaign, or a click before the FC
    // resolved).
    const seededK =
      fc?.type === "FeatureCollection" ? fc.features?.length : undefined;
    useZoneCoreStore.getState().clearDraft(field.field_id);
    useZoneEditStore.getState().start(field.field_id, "relative", {
      fieldName: field.field_name,
      k: typeof seededK === "number" && seededK >= 2 ? seededK : null,
      boundaryAcres: field.acres, // the STORED figure feeds the auto-pick's acres/10 term
      previewFc: fc ?? null, // the card's served AutoMap → zones on screen the moment the editor mounts
    });
    // the map-view intro: the relative panel build plays over the map, then the zones appear —
    // skipped when the user arrived from the animation window they just watched.
    if (enhancedVisuals && !introSeenRef.current)
      useMapIntro.getState().arm(field.field_id, "relative");
    introSeenRef.current = false;
    useUiStore.getState().openMap(field.field_id);
    onClose();
  }

  function onBehaviorZones(fc?: GeoJSON.FeatureCollection) {
    if (multi && !useExisting) {
      startCampaign("behavior");
      return;
    } // >1 field → per-field stepper
    if (useExisting) {
      // upload a zone file → classify each zone into the 27 behavior categories (manual)
      setView("classifyZones");
      return;
    }
    // the interactive CUSTOMIZE editor: adjustable Dry/Mid/Wet · U/M/P · I/M/F thresholds + cell-edit + reclassify.
    usePointEditStore.getState().end();
    useZoneCreateStore.getState().end();
    useBehaviorZoneStore.getState().end();
    useZoneCoreStore.getState().clearDraft(field.field_id);
    useZoneEditStore.getState().start(field.field_id, "behavior", {
      fieldName: field.field_name,
      boundaryAcres: field.acres,
      previewFc: fc ?? null,
    });
    if (enhancedVisuals && !introSeenRef.current)
      useMapIntro.getState().arm(field.field_id, "behavior");
    introSeenRef.current = false;
    useUiStore.getState().openMap(field.field_id);
    onClose();
  }

  function onClassicGrid(fc?: GeoJSON.FeatureCollection) {
    if (multi && !useExisting) {
      startCampaign("grid");
      return;
    } // >1 field → per-field stepper
    if (multi && useExisting) {
      void runForPicks(true);
      return;
    } // >1 field → IDW-build every field from its PICKED event
    if (useExisting) {
      if (selectedEvent && isPointShaped(selectedEvent)) {
        // the ripple window — never the grid layout story, and no Customize.
        return void saveClassicGrid([
          { fieldId: field.field_id, eventId: selectedEvent.event_id },
        ]);
      }
      // using existing samples but no usable points event → upload results, then IDW-map them
      setUploadMethod("idw");
      setView("uploadPoints");
      return;
    }
    // no existing points → lay out a GRID on the map to edit + order. Seeded from the served AutoMap grid
    // (matching the preview) when Customize supplied it, else a fresh gridPoints lattice at the default density.
    // (The IDW map is built later when results come back, via this same school on the results.)
    useZoneCreateStore.getState().end();
    useBehaviorZoneStore.getState().end();
    useZoneEditStore.getState().end();
    seedPointEditorFromServed(fc ?? null, true);
    if (enhancedVisuals && !introSeenRef.current)
      useMapIntro.getState().arm(field.field_id, "grid");
    introSeenRef.current = false;
    useUiStore.getState().openMap(field.field_id);
    onClose();
  }

  // --- uploads (used only when "Use Existing Samples" is on but nothing was selected) ---
  async function saveCsvPoints(result: {
    fc: unknown;
    saved: number;
    skipped: number;
  }) {
    if (result.saved === 0) {
      setNote({
        text: "No rows had a valid latitude/longitude — check the column matching.",
        err: true,
      });
      return;
    }
    setBusy(true);
    try {
      // MULTI-FIELD sequence: save this field's points, advance to the next; after the last, build all fields.
      if (uploadSeq) {
        const target = fields[uploadSeq.index];
        const saved = await api.saveSamplePoints(target.field_id, result.fc);
        void qc.invalidateQueries({ queryKey: qk.samples(target.field_id) });
        void qc.invalidateQueries({
          queryKey: qk.sampleEvents(target.field_id),
        });
        void qc.invalidateQueries({ queryKey: qk.layers(target.field_id) });
        setCsvDraft(null);
        if (saved.normalization === "queued")
          queuedFieldsRef.current.add(target.field_id);
        const nextIndex = uploadSeq.index + 1;
        if (nextIndex < fields.length) {
          setUploadSeq({ ...uploadSeq, index: nextIndex });
          setBusy(false);
          return;
        } // upload the next field
        // all fields uploaded → build the map for each field that is not still queued
        const method = uploadSeq.method;
        const queued = queuedFieldsRef.current;
        const ready = fields.filter((f) => !queued.has(f.field_id));
        setUploadSeq(null);
        setView("menu");
        if (ready.length > 0) {
          if (method === "idw") {
            await saveClassicGrid(
              ready.map((f) => ({ fieldId: f.field_id, eventId: null })),
            );
          } else {
            for (const f of ready) await dispatchModelRun(null, {}, f.field_id);
            if (queued.size === 0) {
              setNote({
                text: `Uploaded points + started the map build for all ${fields.length} fields — they’ll appear in each field’s layer list.`,
                err: false,
              });
            }
          }
        }
        if (queued.size > 0) {
          setNote({
            text: `${SAMPLES_PROCESSING} The map was not started for ${queued.size} of ${fields.length} fields; build those once processing finishes.`,
            err: false,
          });
        }
        queuedFieldsRef.current = new Set();
        return;
      }
      const saved = await api.saveSamplePoints(field.field_id, result.fc);
      void qc.invalidateQueries({ queryKey: qk.samples(field.field_id) });
      void qc.invalidateQueries({ queryKey: qk.sampleEvents(field.field_id) });
      void qc.invalidateQueries({ queryKey: qk.layers(field.field_id) });
      setCsvDraft(null);
      setView("menu");
      if (saved.normalization === "queued") {
        setNote({ text: SAMPLES_PROCESSING, err: false });
        return;
      }
      // map from the freshly uploaded points (now the field's latest event)
      if (uploadMethod === "idw") {
        await saveClassicGrid([{ fieldId: field.field_id, eventId: null }]);
        return;
      }
      await dispatchModelRun(null, {});
    } catch (e) {
      setNote({ text: errMsg(e), err: true });
    } finally {
      setBusy(false);
    }
  }

  async function uploadZonesFile(file: File) {
    setBusy(true);
    setNote(null);
    try {
      const mint = await api.uploadFile(field.field_id, "zones", file);
      void qc.invalidateQueries({ queryKey: qk.sampleEvents(field.field_id) });
      setView("menu");
      // honest about the parse: "parsing now" only when a job was actually filed.
      setNote(zoneUploadNote(mint, file.name));
    } catch (e) {
      setView("menu");
      setNote({ text: errMsg(e), err: true });
    } finally {
      setBusy(false);
    }
  }

  // ---- render -----------------------------------------------------------------------
  const backdrop = (children: ReactNode, onBackdrop: () => void) => (
    <Modal>
      <div
        className="fixed inset-0 z-dialog flex items-center justify-center bg-black/40 p-4"
        onClick={onBackdrop}
      >
        <div
          className="flex max-h-[85vh] w-full max-w-lg flex-col rounded-lg bg-surface text-fg shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          {children}
        </div>
      </div>
    </Modal>
  );
  const modalHeader = (title: string, onX: () => void) => (
    <div className="flex items-center justify-between border-b border-line px-3 py-2">
      <h2 className="text-sm font-semibold">{title}</h2>
      <button
        onClick={onX}
        className="rounded p-1 hover:bg-hover"
        aria-label="Close"
      >
        <X size={16} />
      </button>
    </div>
  );

  if (view === "jdlink")
    return (
      <JdLinkModal fieldId={field.field_id} onClose={() => setView("jd")} />
    );

  if (view === "highresInfo")
    return (
      <HighResInfo
        initialModel={highresModel}
        onClose={() => setView("menu")}
      />
    );
  if (view === "classifyZones")
    return (
      <ClassifyZonesModal
        fieldId={field.field_id}
        onClose={() => {
          setView("menu");
          onClose();
        }}
      />
    );

  if (view === "custom") {
    return backdrop(
      <>
        {modalHeader(`Custom soil mapping · ${field.field_name}`, onClose)}
        <div className="overflow-auto p-4">
          <MapSoilPropsOptions field={field} />
        </div>
      </>,
      onClose,
    );
  }

  if (view === "layerpick") {
    const f = fields[pickIndex] ?? field;
    const raw =
      f.field_id === field.field_id ? sortedEvents : seqEvents[f.field_id];
    const loading = raw === undefined && f.field_id !== field.field_id;
    const evs = [...(raw ?? [])].sort((a, b) =>
      b.created_at.localeCompare(a.created_at),
    );
    // results mode admits only layers WITH attached results; the first pick locks the SHAPE for
    // every later field —; shape not namespace since — both filters, one place.
    const rows = evs
      .filter((ev) => (mode === "results" ? ev.has_results : true))
      .filter((ev) =>
        lockedPointShaped == null
          ? true
          : isPointShaped(ev) === lockedPointShaped,
      );
    const cancel = () => {
      setMode("create");
      setPicks({});
      setEventId(null);
      setView("menu");
    };
    return backdrop(
      <>
        {modalHeader(
          `${mode === "retest" ? "Order Retest" : "Map With Existing Test Results"} — pick a layer${fields.length > 1 ? ` (${pickIndex + 1} of ${fields.length})` : ""}`,
          cancel,
        )}
        <div className="overflow-auto p-3">
          <p className="mb-2 text-[11px] text-muted">
            Pick the {lockedWord ?? "point or zone"} layer for{" "}
            <span className="font-medium text-fg">{f.field_name}</span>
            {mode === "results"
              ? " — only layers with soil results attached are shown."
              : "."}
          </p>
          {pickIndex === 0 && pin.overriding && (
            <div
              role="status"
              className="mb-2 rounded border border-amber-500/40 bg-amber-500/10 px-2 py-1.5 text-[11px]"
            >
              <div className="text-fg">
                This field is still using{" "}
                <span className="font-medium">{pin.current!.name}</span>, but
                newer samples exist and are being treated as old.
              </div>
              <button
                className="mt-1 rounded bg-accent px-2 py-0.5 text-[11px] font-medium text-canvas hover:bg-accent-hover disabled:opacity-50"
                disabled={designate.isPending}
                onClick={() =>
                  designate.mutate({ fieldId: field.field_id, eventId: null })
                }
              >
                {designate.isPending ? "Updating…" : "Use the newest samples"}
              </button>
            </div>
          )}
          {loading && (
            <p className="text-[11px] text-muted">
              <Loader2 size={11} className="mr-1 inline animate-spin" /> Loading
              this field's layers…
            </p>
          )}
          {!loading && rows.length === 0 && (
            <div className="rounded border border-line px-2 py-2 text-[11px] text-muted">
              {mode === "results" ? (
                <>
                  <p>
                    No {lockedWord ?? "point or zone"} layers with results exist
                    on {f.field_name}.
                  </p>
                  <button
                    className="mt-2 rounded bg-accent px-2 py-1 text-[11px] font-medium text-canvas hover:bg-accent-hover"
                    onClick={() => {
                      setUploadMethod("ml");
                      if (fields.length > 1) startUploadSeq("ml");
                      else setView("uploadPoints");
                    }}
                  >
                    Upload a results file instead
                  </button>
                </>
              ) : (
                <p>
                  No {lockedWord ?? "point or zone"} layers exist on{" "}
                  {f.field_name} to retest — create new points/zones instead.
                </p>
              )}
            </div>
          )}
          <ul className="space-y-1">
            {rows.map((ev) => {
              return (
                <li key={ev.event_id}>
                  <button
                    className="flex min-w-0 w-full items-center justify-between gap-2 rounded border border-line px-2 py-1.5 text-left text-[12px] hover:bg-hover disabled:opacity-40"
                    disabled={busy}
                    onClick={() => void pickLayer(f, ev)}
                  >
                    <span className="truncate">
                      {ev.name} · {ev.count} {ev.kind}
                      {ev.has_results ? " · results in" : " · no results yet"}
                    </span>
                    {busy && (
                      <Loader2
                        size={11}
                        className="shrink-0 animate-spin text-faint"
                      />
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
          {/* pickLayer's refusals (no retrievable geometry, fetch errors) land in `note` — this view never rendered it, so a refused pick looked like a dead click. */}
          {note?.err && (
            <p role="alert" className="mt-2 text-[11px] text-red-400">
              {note.text}
            </p>
          )}
        </div>
      </>,
      cancel,
    );
  }

  if (view === "behaviorConfirm") {
    // 'S TEXT, VERBATIM — the confirm exists because this RENAMES the zones the user picked.
    return backdrop(
      <>
        {modalHeader("Behavior-Classified Zones", () => setView("menu"))}
        <div className="max-w-md p-3">
          <p className="text-[12px] text-fg">
            These zones will be named by our calculated moisture, productivity,
            and fertility indexes for that zone. It’s usually best to create new
            zones specifically for this preset. Are you sure?
          </p>
          <div className="mt-3 flex justify-end gap-2">
            <button
              className="rounded border border-line px-3 py-1 text-[12px] hover:bg-hover"
              onClick={() => setView("menu")}
            >
              Cancel
            </button>
            <button
              className="rounded bg-accent px-3 py-1 text-[12px] font-medium text-canvas hover:bg-accent-hover disabled:opacity-50"
              disabled={busy}
              onClick={() =>
                void (mode === "results"
                  ? resultsBehaviorConfirmed()
                  : retestBehaviorConfirmed())
              }
            >
              {busy ? "Classifying…" : "Confirm"}
            </button>
          </div>
        </div>
      </>,
      () => setView("menu"),
    );
  }

  if (view === "picker") {
    return backdrop(
      <>
        {modalHeader("Use existing samples", () => setView("menu"))}
        <div className="overflow-auto p-3">
          <p className="mb-2 text-[11px] text-muted">
            Pick the sample event to map from:
          </p>
          {/* CONSISTENCY CHECK — "just always use the newest". */}
          {pin.overriding && (
            <div
              role="status"
              className="mb-2 rounded border border-amber-500/40 bg-amber-500/10 px-2 py-1.5 text-[11px]"
            >
              <div className="text-fg">
                This field is still using{" "}
                <span className="font-medium">{pin.current!.name}</span>
                {pin.current!.sample_date
                  ? ` (${fmtEventDate(pin.current!.sample_date)})`
                  : ""}
                , but newer samples
                {pin.newest!.sample_date
                  ? ` from ${fmtEventDate(pin.newest!.sample_date)}`
                  : ""}{" "}
                exist and are being treated as old.
              </div>
              <button
                className="mt-1 rounded bg-accent px-2 py-0.5 text-[11px] font-medium text-canvas hover:bg-accent-hover disabled:opacity-50"
                disabled={designate.isPending}
                onClick={() =>
                  designate.mutate({ fieldId: field.field_id, eventId: null })
                }
              >
                {designate.isPending ? "Updating…" : "Use the newest samples"}
              </button>
            </div>
          )}
          {designate.isError && (
            <p role="alert" className="mb-2 text-[11px] text-red-400">
              Could not change the current samples — {errMsg(designate.error)}
            </p>
          )}
          <ul className="space-y-1">
            {sortedEvents.map((ev, i) => (
              <li key={ev.event_id} className="flex items-center gap-1">
                <button
                  className={`flex min-w-0 flex-1 items-center justify-between gap-2 rounded border px-2 py-1.5 text-left text-[12px] hover:bg-hover ${eventId === ev.event_id ? "border-accent bg-accent/10" : "border-line"}`}
                  onClick={() => {
                    setEventId(ev.event_id);
                    setView("menu");
                  }}
                >
                  <span className="truncate">
                    {ev.name} · {ev.count} {ev.kind}
                    {ev.has_results ? " · results in" : " · no results yet"}
                    {eventStatusLabel(ev, i)}
                  </span>
                </button>
                {/*
 make THIS campaign the field's current one (pins it) — only meaningful for a results-bearing
                    event that isn't already current
*/}
              </li>
            ))}
          </ul>
          <button
            className="mt-3 text-[11px] text-muted underline hover:text-fg"
            onClick={() => {
              setEventId(null);
              setView("menu");
            }}
          >
            I’ll upload a file instead — continue
          </button>
        </div>
      </>,
      () => setView("menu"),
    );
  }

  if (view === "uploadPoints") {
    const seqField = uploadSeq ? fields[uploadSeq.index] : null;
    const cancelUpload = () => {
      setUploadSeq(null);
      setCsvDraft(null);
      setView("menu");
    };
    return backdrop(
      <>
        {modalHeader(
          seqField
            ? `Upload points — ${seqField.field_name} (${uploadSeq!.index + 1} of ${fields.length})`
            : "Upload sample points (with results)",
          cancelUpload,
        )}
        <div className="overflow-auto p-3">
          {seqField && (
            <p className="mb-2 text-[11px] text-muted">
              Upload the measured points for{" "}
              <span className="font-medium text-fg">{seqField.field_name}</span>
              , then you’ll do the next field. After the last, we build the map
              for every field.
            </p>
          )}
          {!csvDraft ? (
            <UploadCsvStep
              onParsed={setCsvDraft}
              onCancel={cancelUpload}
              accept=".csv"
              hint="CSV with latitude / longitude columns and measured values."
            />
          ) : (
            <CsvPointsMapper
              parsed={csvDraft}
              onSave={(r) => void saveCsvPoints(r)}
              onCancel={() => {
                setCsvDraft(null);
              }}
            />
          )}
          {note?.err && (
            <p className="mt-2 text-[11px] text-red-400">{note.text}</p>
          )}
        </div>
      </>,
      cancelUpload,
    );
  }

  if (view === "uploadZones") {
    return backdrop(
      <>
        {modalHeader("Upload a zone file", () => setView("menu"))}
        <div className="flex flex-col gap-2 overflow-auto p-4 text-[12px]">
          <p className="text-muted">
            Upload the field’s management zones (.zip / .shp / .kml / .geojson).
          </p>
          <div>
            <button
              disabled={busy}
              className="rounded border border-line px-3 py-1.5 text-fg hover:bg-hover disabled:opacity-50"
              onClick={() => zonesFileRef.current?.click()}
            >
              {busy ? (
                <Loader2 size={12} className="inline animate-spin" />
              ) : null}{" "}
              Choose zone file…
            </button>
            <input
              ref={zonesFileRef}
              type="file"
              accept=".zip,.shp,.kml,.kmz,.geojson,.json"
              className="hidden"
              aria-label="Zone file"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void uploadZonesFile(f);
                if (zonesFileRef.current) zonesFileRef.current.value = "";
              }}
            />
          </div>
          {note?.err && (
            <span className="text-[11px] text-red-400">{note.text}</span>
          )}
        </div>
      </>,
      () => setView("menu"),
    );
  }

  if (view === "uploadCores") {
    return backdrop(
      <>
        {modalHeader("Upload core locations", () => setView("menu"))}
        <div className="flex flex-col gap-2 overflow-auto p-4 text-[12px]">
          <p className="text-muted">
            Upload the cores for {field.field_name}. {CORE_FILE_HINT} The Genius
            Zones map starts as soon as the file is read.
          </p>
          <div>
            <button
              disabled={busy}
              className="rounded border border-line px-3 py-1.5 text-fg hover:bg-hover disabled:opacity-50"
              onClick={() => coresFileRef.current?.click()}
            >
              {busy ? (
                <Loader2 size={12} className="inline animate-spin" />
              ) : null}{" "}
              Choose cores file…
            </button>
            <input
              ref={coresFileRef}
              type="file"
              accept={CORE_FILE_ACCEPT}
              className="hidden"
              aria-label="Cores file"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void runGeniusFromCoreFile(f);
                if (coresFileRef.current) coresFileRef.current.value = "";
              }}
            />
          </div>
          {note && (
            <span
              className={`text-[11px] ${note.err ? "text-red-400" : "text-muted"}`}
            >
              {note.text}
            </span>
          )}
        </div>
      </>,
      () => {
        if (!busy) setView("menu");
      },
    );
  }

  if (view === "jd") {
    return backdrop(
      <JdImportStep
        field={field}
        title="Add John Deere data first?"
        intro="This field has no John Deere data yet. Bringing in machine and yield layers improves the soil map — it’s optional."
        onLink={() => setView("jdlink")}
        onDone={() => {
          setView("menu");
          proceedHighRes();
        }}
        onSkip={() => {
          setView("menu");
          proceedHighRes();
        }}
        onClose={() => setView("menu")}
      />,
      () => setView("menu"),
    );
  }

  // the inline "Map Soil" panel: 4 interactive preview maps Renders in flow BETWEEN the Map Soil
  // button and the table (the table slides down), NOT a full-screen overlay.
  /*
   *
   * The animation opens ALONGSIDE the normal path, never instead of it — with Enhanced Visuals off
   * every one of these behaves exactly as it does today.
   */
  const defaultClick =
    (
      kind: "points" | "zones",
      mode: CampaignMode,
      customize: (fc?: GeoJSON.FeatureCollection) => void,
      automapId?: string,
      variant?: AnimVariant,
      animCustomize?: (fc?: GeoJSON.FeatureCollection) => void,
    ) =>
    (fc?: GeoJSON.FeatureCollection) => {
      if (useExisting) {
        customize();
        return;
      }
      if (variant && enhancedVisuals) {
        animCustomizeRef.current = animCustomize ?? customize;
        setAnimOpen(variant);
      }
      void confirmPresetOrder(kind, mode, fc, automapId);
    };

  const rk = resolveReadingKeys(covs ?? []);
  // Genius Map's underlayResolve is only the fallback. The preview paints whichever uncorrected
  // P/K/pH/OM/CEC model has the widest two-sided range around its standard — the same pick as Genius Zones.
  const baseSchools: PreviewSchool[] = [
    {
      id: "highres",
      title: "Genius Map",
      compareTo: HIGHRES_COMPARE_TO,
      favorite: true,
      description: [HIGHRES_INTRO, ...HIGHRES_BULLETS].join("\n"),
      automapId: "automap-suggested-points",
      automapKind: "points",
      underlayResolve: findOmModel,
      readingKeys: rk.soil,
      onCustomize: onHighRes,
      onDefault: defaultClick(
        "points",
        "suggest",
        onHighRes,
        "automap-suggested-points",
        "sample",
        (fc) => onHighRes(fc, true),
      ),
      infoNote: "Select a Model to Learn More:",
      modelButtons: MODEL_GAINS.map((m) => ({
        key: m.key,
        label: m.label,
        onClick: () => {
          setHighresModel(m.key);
          setView("highresInfo");
        },
      })),
    },
    {
      id: "fieldrel",
      title: "Genius Zones",
      compareTo: "Baseline Rx, FieldAlytics Zones",
      description: DESC.zones,
      automapId: "automap-relative-zones",
      automapKind: "relative",
      readingKeys: rk.drivers,
      onCustomize: onZones,
      onDefault: defaultClick(
        "zones",
        "zones",
        onZones,
        "automap-relative-zones",
        "relative",
      ),
    },
    {
      id: "grid",
      title: "Classic Grid (Purely Spatial)",
      compareTo: "Traditional Grid Sampling",
      description: DESC.classicGrid,
      automapId: "automap-grid-points",
      automapKind: "points",
      underlayResolve: findGridOmIdw,
      readingKeys: resolveGridIdwReadings(covs ?? []),
      onCustomize: onClassicGrid,
      onDefault: defaultClick(
        "points",
        "grid",
        onClassicGrid,
        "automap-grid-points",
        "grid",
      ),
    },
    {
      id: "relative",
      title: "Multi-Input, Relative Zones",
      compareTo: "Baseline Rx, FieldAlytics Zones",
      description: DESC.zones,
      automapId: "automap-relative-zones",
      automapKind: "relative",
      readingKeys: rk.drivers,
      onCustomize: onZones,
      onDefault: defaultClick(
        "zones",
        "zones",
        onZones,
        "automap-relative-zones",
        "relative",
      ),
    },
    {
      id: "behavior",
      title: "Behavior-Classified Zones",
      compareTo: "FieldReveal MZB, SWAT Maps",
      description: BEHAVIOR_DESC,
      automapId: "automap-decile-zones",
      automapKind: "behavior",
      readingKeys: rk.behavior,
      onCustomize: onBehaviorZones,
      onDefault: defaultClick(
        "zones",
        "behavior",
        onBehaviorZones,
        "automap-decile-zones",
        "behavior",
      ),
    },
  ];
  const isRelativeSchool = (id: PreviewSchool["id"]) =>
    id === "relative" || id === "fieldrel";

  /*
   *
   * THE PICKED TYPE FILTERS THE CARDS: points → High-Res + Classic Grid; zones → Behavior + both
   * relative-zone cards. And each mode rewrites what the two buttons DO: create → today's handlers
   * untouched.
   */
  const allSchools = baseSchools;
  const schools: PreviewSchool[] = (() => {
    // `kind` is BE-Light's event-id PREFIX, not a closed enum — a bulk-ingested layer arrives as
    // `'bulk'` (`bulk:<session>:<uid>`).
    const filtered =
      mode !== "create" && lockedPointShaped !== null
        ? baseSchools.filter((sc) =>
            lockedPointShaped
              ? sc.id === "highres" || sc.id === "grid"
              : sc.id === "behavior" || isRelativeSchool(sc.id),
          )
        : baseSchools;
    if (mode === "create") return filtered;
    // THE PICKED LAYER IS THE PICTURE: once a points/zone layer is picked, every card shows THAT
    // layer instead of its AutoMap — the presets act on the picked layer.
    const picked = picks[field.field_id]?.fc ?? null;
    const captionOf = (id: PreviewSchool["id"]): string | undefined =>
      mode === "results" && picked ? pickedCaption(id) : undefined;
    return filtered.map((sc) => ({
      ...sc,
      overrideFc: picked,
      caption: captionOf(sc.id),
      customizeLabel:
        mode === "results" && sc.id === "fieldrel"
          ? "Pick Core Points"
          : undefined,
      onCustomize:
        mode === "results"
          ? sc.id === "fieldrel"
            ? () => resultsCustomizeGeniusZones()
            : undefined
          : mode === "retest"
            ? sc.id === "highres"
              ? () => retestCustomizePoints(false)
              : sc.id === "grid"
                ? () => retestCustomizePoints(true)
                : isRelativeSchool(sc.id)
                  ? () => retestCustomizeZones()
                  : undefined // Behavior: no Customize in retest, per spec.
            : undefined,
      onDefault:
        mode === "retest"
          ? sc.id === "behavior"
            ? () => setView("behaviorConfirm")
            : isRelativeSchool(sc.id)
              ? () => retestOrder("zones")
              : () => retestOrder("points")
          : sc.id === "fieldrel"
            ? () => openGeniusCoresUpload()
            : sc.id === "behavior"
              ? () => setView("behaviorConfirm") // results: confirm → rename → animation + flat build
              : sc.id === "relative"
                ? () => void resultsRunZonesFlat("relative")
                : sc.onDefault, // results points schools: today's use-existing routes (single) / runForPicks (multi)
    }));
  })();
  return (
    <div className="mt-2 flex flex-col overflow-hidden rounded-lg border border-line bg-surface text-fg shadow-md">
      {/* Rendered here rather than replacing the panel: the dispatch has already happened by the time this shows, and closing it must leave everything else exactly as it was. */}
      {savingOpen && (
        <SavingSoilMapNote
          fieldCount={fields.length}
          onClose={() => setSavingOpen(false)}
        />
      )}
      {rippleOpen && (
        <IdwRippleWindow
          fieldId={field.field_id}
          fieldCount={fields.length}
          points={picks[field.field_id]?.fc ?? null}
          eventId={
            picks[field.field_id]?.eventId ?? selectedEvent?.event_id ?? null
          }
          onClose={() => setRippleOpen(false)}
        />
      )}
      {animOpen && (
        <MapSoilAnimation
          variant={animOpen}
          loop={animLoop}
          fieldCount={fields.length}
          cells={anim.cells}
          labels={anim.labels}
          status={anim.status}
          aspect={anim.aspect}
          bbox={anim.bbox}
          // the served plane keys + ramps — the Behavior stage addresses planes BY NAME
          keys={anim.keys}
          ramps={anim.ramps}
          // Seeded with the design the user was LOOKING at — `onHighRes` keeps it as the editor's
          // starting points, so Customize continues that design rather than resolving another one.
          onCustomize={
            animOpen && animOpen !== "map"
              ? (fc) => {
                  setAnimOpen(null);
                  setAnimLoop(false);
                  // coming FROM the animation window means they just watched this school's story — the
                  // map-view intro must not replay it (same rule as High-Res, now for every school).
                  introSeenRef.current = true;
                  animCustomizeRef.current?.(fc);
                }
              : undefined
          }
          onClose={() => {
            setAnimOpen(null);
            setAnimLoop(false);
          }}
        />
      )}
      <div className="border-b border-line px-3 py-2 text-center">
        <h1 className="text-lg font-bold text-fg sm:text-xl">
          How Do You Want to Map Your Soil?
        </h1>
      </div>
      {/* header: title left, the sample-source toggle CENTERED, close right. */}
      <div className="relative flex items-center gap-3 border-b border-line px-3 py-1.5">
        <h2 className="text-[13px] font-semibold">
          Map Soil{" "}
          <span className="font-normal text-muted">— {field.field_name}</span>
        </h2>
        {multi && (
          <span className="text-[11px] text-faint">
            showing the first of {fields.length} fields — the method you pick
            applies to all {fields.length}
          </span>
        )}
        {note && (
          <span
            className={`text-[11px] ${note.err ? "text-red-400" : "text-muted"}`}
          >
            {note.text}
            {note.retry ? (
              <button
                type="button"
                className="ml-2 underline"
                onClick={note.retry}
              >
                Retry
              </button>
            ) : null}
          </span>
        )}
        {busy && (
          <span className="flex items-center gap-1 text-[11px] text-muted">
            <Loader2 size={11} className="animate-spin text-accent" /> working…
          </span>
        )}
        <div className="absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 items-center gap-2">
          <div
            className="inline-flex overflow-hidden rounded border border-line text-[12px]"
            role="group"
            aria-label="Sample source"
          >
            {(
              [
                ["create", "Create New Points/Zones"],
                ["retest", "Order Retest"],
                ["results", "Map With Existing Test Results"],
              ] as const
            ).map(([m, label]) => (
              <button
                key={m}
                className={`px-2.5 py-0.5 ${mode === m ? "bg-accent font-medium text-canvas" : "text-muted hover:bg-hover"}`}
                aria-pressed={mode === m}
                onClick={() => switchMode(m)}
              >
                {label}
              </button>
            ))}
          </div>
          {mode !== "create" && (
            <span className="flex items-center gap-2 text-[11px] text-faint">
              {lockedWord
                ? `${lockedWord}s · ${Object.keys(picks).length}/${fields.length} field${fields.length === 1 ? "" : "s"}`
                : "no layer picked yet"}
              <button
                className="text-muted underline hover:text-fg"
                onClick={() => {
                  setPicks({});
                  setPickIndex(0);
                  setView("layerpick");
                }}
              >
                change
              </button>
            </span>
          )}
        </div>
        <button
          onClick={onClose}
          className="ml-auto rounded p-1 hover:bg-hover"
          aria-label="Close"
        >
          <X size={16} />
        </button>
      </div>

      {/* the preview maps — 2 columns, a third row when all five show. Bounded height so the table stays visible. */}
      {/* WebGL context, and the mode toggle re-filtering `schools` was unmount/remount churn — released contexts free LAZILY, so rapid toggling stacked live contexts past Chrome's ~16 ceiling force-losing the oldest until the page was BLOCKED from creating any. */}
      {/* Map-With-Existing subtitle: what the two cards below are choosing between. */}
      {mode === "results" && picks[field.field_id]?.fc && (
        <p className="px-3 pt-2 text-center text-[12px] font-semibold text-fg">
          {pickedSubtitle(picks[field.field_id]!.pointShaped)}
        </p>
      )}
      <div
        className={`grid grid-cols-2 ${schools.length > 4 ? "grid-rows-3" : schools.length > 2 ? "grid-rows-2" : "grid-rows-1"} gap-3 p-3`}
        style={{
          height:
            schools.length > 4
              ? "min(640px, 74vh)"
              : schools.length > 2
                ? "min(440px, 56vh)"
                : "min(260px, 34vh)",
        }}
      >
        {allSchools.map((s) => {
          const shown = schools.some((v) => v.id === s.id);
          const live = schools.find((v) => v.id === s.id) ?? s;
          return (
            <div
              key={s.id}
              className={shown ? "flex min-h-0 flex-col" : "hidden"}
            >
              {/* the caption slot renders only when there is one; the ONE-CELL GRID below stretches the card exactly as the outer grid did, and the wrapper shape never changes with the caption — a. */}
              {live.caption && (
                <p
                  className="mb-1 truncate text-center text-[10px] font-medium text-muted"
                  title={live.caption}
                >
                  {live.caption}
                </p>
              )}
              <div className="grid min-h-0 flex-1">
                <PreviewMap field={field} school={live} />
              </div>
            </div>
          );
        })}
      </div>

      {showsAdvancedControls(uiMode) && (
        <div className="relative flex items-center justify-center border-t border-line px-3 py-1.5">
          <button
            onClick={() => setView("custom")}
            className="flex items-center gap-2 rounded border border-line px-3 py-1 text-[12px] text-fg hover:bg-hover"
          >
            <SlidersHorizontal size={14} /> Custom — full manual control
          </button>
          <span className="absolute right-3 hidden text-[11px] text-faint sm:block">
            Click any map to start that method.
          </span>
        </div>
      )}
    </div>
  );
}

// A minimal "choose a CSV file → parse" step shared by the upload views.
function UploadCsvStep({
  onParsed,
  onCancel,
  accept,
  hint,
}: {
  onParsed: (p: ParsedCsv) => void;
  onCancel: () => void;
  accept: string;
  hint: string;
}) {
  const ref = useRef<HTMLInputElement>(null);
  const [err, setErr] = useState<string | null>(null);
  return (
    <div className="flex flex-col gap-2 text-[12px]">
      <p className="text-muted">{hint}</p>
      <div className="flex items-center gap-2">
        <button
          className="rounded border border-line px-3 py-1.5 text-fg hover:bg-hover"
          onClick={() => ref.current?.click()}
        >
          Choose file…
        </button>
        <button
          className="text-[11px] text-muted hover:text-fg"
          onClick={onCancel}
        >
          Cancel
        </button>
      </div>
      <input
        ref={ref}
        type="file"
        accept={accept}
        className="hidden"
        aria-label="Sample points CSV"
        onChange={async (e) => {
          const f = e.target.files?.[0];
          if (ref.current) ref.current.value = "";
          if (!f) return;
          try {
            const p = parseCsv(await f.text(), f.name);
            if (!p.headers.length || !p.rows.length) {
              setErr("That CSV appears to be empty.");
              return;
            }
            onParsed(p);
          } catch (e) {
            setErr(
              e instanceof Error ? e.message : "Could not read that file.",
            );
          }
        }}
      />
      {err && <span className="text-red-400">{err}</span>}
    </div>
  );
}

// D4 — optional John Deere import offered when the field has no JD data. Mirrors the connection
// lifecycle: not connected → sign in; connected but this field isn't linked → link; linked → import.
